// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

export type SwConfig = {
  /** Fired after first install when no previous controller existed. */
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  /** Fired as soon as we detect a new SW being installed (download/install in progress). */
  onUpdateInstalling?: (registration: ServiceWorkerRegistration) => void;
  /** Fired when a new SW is installed and waiting to activate. */
  onUpdateReady?: (registration: ServiceWorkerRegistration) => void;
  /** Fired when the active controller changes (i.e. update has taken effect). Safe to reload. */
  onControllerChange?: () => void;
};

// Track whether we've already handled a controllerchange event to avoid reload loops.
let refreshing = false;

/**
 * Tell a waiting service worker to take control. The page will receive a
 * `controllerchange` event when the new worker becomes active.
 */
export function applyUpdate(registration: ServiceWorkerRegistration): void {
  if (registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

export function register(config?: SwConfig) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    // Listen for controller changes once. This fires when SKIP_WAITING + clientsClaim()
    // hands the page over to the new service worker.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      if (config?.onControllerChange) {
        config.onControllerChange();
      }
    });

    const startRegistration = () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    };

    // The original CRA template called `register()` at module-evaluation time,
    // so deferring with the `load` event was safe. We now call it from a React
    // effect (after first paint), by which point `load` has already fired and
    // the listener would never run. Detect that case and start immediately.
    if (document.readyState === 'complete') {
      startRegistration();
    } else {
      window.addEventListener('load', startRegistration, { once: true });
    }
  }
}

function registerValidSW(swUrl: string, config?: SwConfig) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // If a worker is already waiting (e.g. installed in a previous session
      // but never activated), surface it immediately.
      if (registration.waiting && navigator.serviceWorker.controller) {
        config?.onUpdateReady?.(registration);
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        // Notify UI that an update is being downloaded/installed (only when there
        // was an existing controller — fresh installs don't count as "updates").
        if (navigator.serviceWorker.controller) {
          config?.onUpdateInstalling?.(registration);
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until activation.
              console.log(
                'New content is available; waiting for user to apply update.'
              );
              config?.onUpdateReady?.(registration);
            } else {
              // First install: everything has been precached for offline use.
              console.log('Content is cached for offline use.');
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: SwConfig) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' }
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
